import { useNavigate } from "react-router-dom";
import { TreeViewWrapper } from "./TreeView.style";
import { Tree } from "react-arborist";
import { FC } from "react";
import { data } from "constants/tree";
import { useMediaQuery, useTheme } from "@mui/material";
import { NodeProps } from "./TreeView.types";
import { FileKind } from "constants/enums";
import { useStoreContext } from "context/storeContext";

type ITreeViewProps = {
  search: string;
  toggleDrawer?: () => void;
};

export const TreeView: FC<ITreeViewProps> = ({ search, toggleDrawer }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    states: { height },
  } = useStoreContext();

  const Node = ({ node, style, dragHandle }: NodeProps) => {
    const { kind, url } = node.data.metadata;
    return (
      <div
        style={style}
        title={node.data.name}
        className="node"
        ref={dragHandle}
        onClick={(e) => {
          e.stopPropagation();
          node.toggle();
          if (
            kind !== FileKind.FOLDER &&
            kind !== FileKind.CLASS &&
            kind !== FileKind.TEMPLATE
          ) {
            console.log("onclick");
            toggleDrawer?.();
          }
          navigate(`${kind}/${url}`);
        }}
      >
        {node.isLeaf
          ? FileIcon(node.data.metadata.kind)
          : FolderIcon(node.isOpen, kind)}
        {node.data.name}
      </div>
    );
  };

  const vhToPixels = (vhValue: number) => {
    const viewportHeight = window.innerHeight;
    return (vhValue * viewportHeight) / 100;
  };

  const compHeight = vhToPixels(80);

  return (
    <TreeViewWrapper className="directory" data-testid="directory-treeview">
      <Tree
        initialData={data}
        openByDefault={false}
        padding={10}
        height={compHeight < height ? height : compHeight}
        width={isTablet ? "30vw" : isMobile ? "100vw" : "20vw"}
        disableDrag
        disableDrop
        disableEdit
        className="tree"
        disableMultiSelection
        searchTerm={search}
        searchMatch={(node, term) =>
          node.data.metadata.kind !== FileKind.FOLDER &&
          node.data.name.toLowerCase().includes(term.toLowerCase())
        }
      >
        {Node}
      </Tree>
    </TreeViewWrapper>
  );
};

const FolderIcon = (isOpen: boolean, kind: string) =>
  kind === "folder" ? (
    <img
      src={`/quantlab-function-library/icons/${isOpen ? "open" : "close"}-folder.svg`}
      className="icon"
      alt="icon"
    />
  ) : (
    FileIcon(kind, isOpen)
  );

const icons: Record<string, string> = {
  keyword: "/quantlab-function-library/icons/alpha-k-box-outline.svg",
  func: "/quantlab-function-library/icons/alpha-f-box-outline.svg",
  memfunc: "/quantlab-function-library/icons/alpha-f-box-outline.svg",
  class_enum: "/quantlab-function-library/icons/alpha-e-box-outline.svg",
  enum: "/quantlab-function-library/icons/alpha-e-box-outline.svg",
  constructor: "/quantlab-function-library/icons/alpha-c-box-outline.svg",
  constant: "/quantlab-function-library/icons/alpha-c-box-outline.svg",
  basic_type: "/quantlab-function-library/icons/alpha-b-box-outline.svg",
  typedef: "/quantlab-function-library/icons/alpha-t-box-outline.svg",
};

const expandIcon = (isOpen?: boolean) =>
  `/quantlab-function-library/icons/round-expand-${isOpen ? "less" : "more"}.svg`;

const FileIcon = (kind: string, isOpen?: boolean) => {
  if (
    [FileKind.TYPE, FileKind.CLASS, FileKind.TEMPLATE].includes(
      kind as FileKind,
    )
  ) {
    return <img src={expandIcon(isOpen)} className="icon" alt="icon" />;
  }
  return <img src={icons[kind]} className="icon" alt="icon" />;
};
